import React, { useState } from 'react';
import type { FC } from 'react';

import { useApolloClient } from '@apollo/client';
import { Trans, useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import AtsName from 'src/entities/Ats/AtsName';
import AtsScorecardName from 'src/entities/Ats/AtsScorecardName';

import useDirectoryInfo from 'src/hooks/useDirectoryInfo';

import cancelDebrief from 'src/utils/api/cancelDebrief';
import cancelInterview from 'src/utils/api/cancelInterview';
import { apolloCacheDeleteById, apolloCacheDeleteByQuery } from 'src/utils/apolloHelpers';
import logError from 'src/utils/logError';

interface Props {
  interviewId: string;
  isOpen: boolean;
  isCompleted?: boolean;
  isDebrief?: boolean;
  onCancel: () => void;
  onClose: () => void;
  selfScheduleOptionsId?: string;
  applicationId?: string;
  jobStageId?: string;
}

const CancelInterviewDialog: FC<Props> = ({
  interviewId,
  isOpen,
  isCompleted,
  isDebrief,
  onCancel,
  onClose,
  selfScheduleOptionsId,
  applicationId,
  jobStageId,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const directoryInfo = useDirectoryInfo();

  const client = useApolloClient();

  const [canceling, setCanceling] = useState<boolean>(false);

  let title = t('hiring-dashboard-candidates:candidates.cancelInterview.titleCancelInterview');
  if (isCompleted) {
    title = t('hiring-dashboard-candidates:candidates.cancelInterview.titleMarkAsCanceled');
  } else if (isDebrief) {
    title = t('hiring-dashboard-candidates:candidates.cancelInterview.titleCancelDebrief');
  }

  const doCancel = async () => {
    setCanceling(true);
    try {
      if (isDebrief) {
        await cancelDebrief({ applicationDebriefId: interviewId });
        apolloCacheDeleteById(client.cache, interviewId, 'ApplicationDebrief');
      } else {
        await cancelInterview({ applicationStageInterviewId: interviewId });
      }
      if (selfScheduleOptionsId) {
        apolloCacheDeleteByQuery(client.cache, 'selfScheduleOptions', { id: selfScheduleOptionsId });
        if (applicationId) {
          apolloCacheDeleteByQuery(client.cache, 'selfScheduleOptionsForApplicationJobStage', {
            input: {
              applicationId,
              jobStageId,
            },
          });
        }
      }
      onCancel();
    } catch (e) {
      logError(e);
      const errorMsg =
        getExternalErrorMessage(e) ||
        (isDebrief
          ? t('hiring-dashboard-candidates:candidates.cancelInterview.failedToCancelDebrief')
          : t('hiring-dashboard-candidates:candidates.cancelInterview.failedToCancelInterview'));
      onClose();
      enqueueSnackbar(errorMsg, {
        variant: 'error',
      });
    }
    setCanceling(false);
  };

  return (
    <Dialog
      title={title}
      subTitle={t('hiring-dashboard-candidates:candidates.cancelInterview.cannotBeUndone')}
      open={isOpen}
      onClose={onClose}
      submitOptions={{
        label: t('hiring-dashboard-candidates:candidates.cancelInterview.cancelEvent'),
        isDisabled: canceling,
        isLoading: canceling,
        isDangerous: true,
        onClick: doCancel,
      }}
      cancelOptions={{
        label: t('hiring-dashboard-candidates:candidates.cancelInterview.keepEvent'),
        onClick: onClose,
      }}
    >
      <Box>
        {!isDebrief && (
          <Typography>
            <Trans
              i18nKey="hiring-dashboard-candidates:candidates.cancelInterview.interviewDeleteWarning"
              values={{ directoryLabel: directoryInfo.label }}
              components={{ atsName: <AtsName />, scorecardName: <AtsScorecardName /> }}
            />
          </Typography>
        )}
        {isDebrief && (
          <Typography>
            {t('hiring-dashboard-candidates:candidates.cancelInterview.debriefDeleteWarning', {
              directoryLabel: directoryInfo.label,
            })}
          </Typography>
        )}
        {isDebrief && <br />}
        {isDebrief && (
          <Typography>{t('hiring-dashboard-candidates:candidates.cancelInterview.debriefNoCommunication')}</Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default CancelInterviewDialog;
